import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Store, Plus, Loader2, ExternalLink, Copy, Check, RefreshCw, Wallet, DollarSign,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";
import DashLayout from "@/components/xuma/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const PLAN_LABELS = {
  basic: "Básico ($699 / $99)", premium: "Premium ($1,499 / $299)",
  founder_basic: "Fundador Básico ($499 / $59)", founder_premium: "Fundador Premium ($999 / $199)",
};
const commTypeLabel = (t) => (t === "config" ? "Configuración (75%)" : "Mensual (30%)");

export default function AdminDistributors() {
  const { user, loading } = useAuth();
  const [dists, setDists] = useState([]);
  const [sales, setSales] = useState([]);
  const [comms, setComms] = useState([]);
  const [newDist, setNewDist] = useState(false);
  const [newSale, setNewSale] = useState(false);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [d, s, c] = await Promise.all([
      api.get("/admin/distributors"), api.get("/admin/sales"), api.get("/admin/commissions"),
    ]);
    setDists(d.data); setSales(s.data); setComms(c.data);
  };
  useEffect(() => { if (user?.role === "admin") load().catch(() => {}); }, [user]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-xuma-primary" /></div>;
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;

  const totalPending = comms.filter((c) => c.status === "pending").reduce((a, c) => a + c.amount, 0);
  const totalPaid = comms.filter((c) => c.status === "paid").reduce((a, c) => a + c.amount, 0);

  const toggleSale = async (sale) => {
    const status = sale.status === "active" ? "cancelled" : "active";
    await api.put(`/admin/sales/${sale.sale_id}`, { status });
    setSales((ss) => ss.map((x) => (x.sale_id === sale.sale_id ? { ...x, status } : x)));
  };
  const toggleComm = async (c) => {
    const status = c.status === "paid" ? "pending" : "paid";
    await api.put(`/admin/commissions/${c.commission_id}`, { status });
    setComms((cs) => cs.map((x) => (x.commission_id === c.commission_id ? { ...x, status } : x)));
  };
  const generateMonthly = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/commissions/generate-monthly");
      toast.success(`${data.created} comisiones mensuales generadas (${data.period})`);
      await load();
    } finally { setBusy(false); }
  };

  return (
    <DashLayout title="Distribuidores">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Store} label="Distribuidores" value={dists.length} />
        <Kpi icon={DollarSign} label="Ventas" value={sales.length} />
        <Kpi icon={Wallet} label="Comisión pendiente" value={money(totalPending)} />
        <Kpi icon={Check} label="Comisión pagada" value={money(totalPaid)} />
      </div>

      <Tabs defaultValue="dist">
        <TabsList className="bg-white border border-xuma-border">
          <TabsTrigger value="dist" data-testid="tab-dist">Distribuidores</TabsTrigger>
          <TabsTrigger value="ventas" data-testid="tab-ventas">Ventas</TabsTrigger>
          <TabsTrigger value="comisiones" data-testid="tab-comisiones">Comisiones</TabsTrigger>
        </TabsList>

        {/* DISTRIBUIDORES */}
        <TabsContent value="dist" className="mt-5">
          <div className="flex justify-end mb-3">
            <Button data-testid="new-distributor-btn" onClick={() => setNewDist(true)} className="bg-xuma-primary hover:bg-xuma-primaryHover text-white rounded-full gap-1.5">
              <Plus className="h-4 w-4" /> Nuevo distribuidor
            </Button>
          </div>
          <Table headers={["Distribuidor", "Clientes", "Activos", "Pendiente", "Pagado", "Tarjeta"]}>
            {dists.map((d) => (
              <tr key={d.user_id} data-testid={`dist-row-${d.user_id}`} className="border-b border-xuma-border last:border-0">
                <td className="px-4 py-3"><p className="font-medium text-xuma-dark">{d.name}</p><p className="text-xs text-xuma-muted">{d.email}</p></td>
                <td className="px-4 py-3">{d.totals.clients}</td>
                <td className="px-4 py-3">{d.totals.active_clients}</td>
                <td className="px-4 py-3 text-xuma-primary font-medium">{money(d.totals.pending)}</td>
                <td className="px-4 py-3 text-green-700">{money(d.totals.paid)}</td>
                <td className="px-4 py-3">{d.card_slug && <a href={`/c/${d.card_slug}`} target="_blank" rel="noreferrer" className="text-xuma-primary inline-flex items-center gap-1">Ver <ExternalLink className="h-3.5 w-3.5" /></a>}</td>
              </tr>
            ))}
            {dists.length === 0 && <EmptyRow cols={6} text="Aún no hay distribuidores." />}
          </Table>
        </TabsContent>

        {/* VENTAS */}
        <TabsContent value="ventas" className="mt-5">
          <div className="flex justify-end mb-3">
            <Button data-testid="new-sale-btn" onClick={() => setNewSale(true)} disabled={dists.length === 0} className="bg-xuma-primary hover:bg-xuma-primaryHover text-white rounded-full gap-1.5">
              <Plus className="h-4 w-4" /> Registrar venta
            </Button>
          </div>
          <Table headers={["Cliente", "Distribuidor", "Plan", "Config", "Mensual", "Estado"]}>
            {sales.map((s) => (
              <tr key={s.sale_id} data-testid={`sale-row-${s.sale_id}`} className="border-b border-xuma-border last:border-0">
                <td className="px-4 py-3"><p className="font-medium text-xuma-dark">{s.client_name}</p><p className="text-xs text-xuma-muted">{s.client_email || "—"}</p></td>
                <td className="px-4 py-3 text-xuma-muted">{s.distributor_name}</td>
                <td className="px-4 py-3">{s.plan_label}</td>
                <td className="px-4 py-3">{money(s.config_fee)}</td>
                <td className="px-4 py-3">{money(s.monthly_fee)}</td>
                <td className="px-4 py-3">
                  <button data-testid={`toggle-sale-${s.sale_id}`} onClick={() => toggleSale(s)}
                    className={`text-xs rounded-full px-3 py-1 font-medium ${s.status === "active" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                    {s.status === "active" ? "Activo" : "Cancelado"}
                  </button>
                </td>
              </tr>
            ))}
            {sales.length === 0 && <EmptyRow cols={6} text="Aún no hay ventas registradas." />}
          </Table>
        </TabsContent>

        {/* COMISIONES */}
        <TabsContent value="comisiones" className="mt-5">
          <div className="flex justify-end mb-3">
            <Button data-testid="generate-monthly-btn" onClick={generateMonthly} disabled={busy} variant="outline" className="rounded-full gap-1.5 border-xuma-border">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Generar comisiones del mes
            </Button>
          </div>
          <Table headers={["Cliente", "Tipo", "Periodo", "Base", "Monto", "Estado"]}>
            {comms.map((c) => (
              <tr key={c.commission_id} data-testid={`comm-row-${c.commission_id}`} className="border-b border-xuma-border last:border-0">
                <td className="px-4 py-3 font-medium text-xuma-dark">{c.client_name}</td>
                <td className="px-4 py-3 text-xuma-muted">{commTypeLabel(c.type)}</td>
                <td className="px-4 py-3 text-xuma-muted">{c.period}</td>
                <td className="px-4 py-3">{money(c.base)}</td>
                <td className="px-4 py-3 font-medium">{money(c.amount)}</td>
                <td className="px-4 py-3">
                  <button data-testid={`toggle-comm-${c.commission_id}`} onClick={() => toggleComm(c)}
                    className={`text-xs rounded-full px-3 py-1 font-medium ${c.status === "paid" ? "bg-green-50 text-green-700" : "bg-xuma-accent/20 text-xuma-dark"}`}>
                    {c.status === "paid" ? "Pagada" : "Pendiente"}
                  </button>
                </td>
              </tr>
            ))}
            {comms.length === 0 && <EmptyRow cols={6} text="Aún no hay comisiones." />}
          </Table>
        </TabsContent>
      </Tabs>

      <NewDistributorDialog open={newDist} onOpenChange={setNewDist} onCreated={(res) => { setCreated(res); load(); }} />
      <NewSaleDialog open={newSale} onOpenChange={setNewSale} dists={dists} onCreated={() => load()} />
      <CredentialsDialog created={created} onClose={() => setCreated(null)} />
    </DashLayout>
  );
}

function NewDistributorDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/distributors", form);
      onOpenChange(false); setForm({ name: "", email: "", phone: "" });
      onCreated(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Nuevo distribuidor</DialogTitle>
          <DialogDescription>Se creará su cuenta y su tarjeta XUMA automáticamente.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mb-1 block">Nombre</Label><Input data-testid="dist-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label className="mb-1 block">Correo</Label><Input data-testid="dist-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label className="mb-1 block">Teléfono (opcional)</Label><Input data-testid="dist-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button data-testid="dist-submit" onClick={submit} disabled={saving || !form.name || !form.email} className="bg-xuma-primary hover:bg-xuma-primaryHover text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear distribuidor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewSaleDialog({ open, onOpenChange, dists, onCreated }) {
  const [form, setForm] = useState({ distributor_id: "", client_name: "", client_email: "", plan: "basic" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/admin/sales", form);
      toast.success("Venta registrada");
      onOpenChange(false); setForm({ distributor_id: "", client_name: "", client_email: "", plan: "basic" });
      onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Registrar venta</DialogTitle>
          <DialogDescription>La comisión de configuración (75%) se genera automáticamente.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mb-1 block">Distribuidor</Label>
            <Select value={form.distributor_id} onValueChange={(v) => setForm({ ...form, distributor_id: v })}>
              <SelectTrigger data-testid="sale-dist"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>{dists.map((d) => <SelectItem key={d.user_id} value={d.user_id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="mb-1 block">Nombre del cliente</Label><Input data-testid="sale-client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
          <div><Label className="mb-1 block">Correo del cliente (opcional)</Label><Input data-testid="sale-email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} /></div>
          <div><Label className="mb-1 block">Plan</Label>
            <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
              <SelectTrigger data-testid="sale-plan"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PLAN_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="sale-submit" onClick={submit} disabled={saving || !form.distributor_id || !form.client_name} className="bg-xuma-primary hover:bg-xuma-primaryHover text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar venta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CredentialsDialog({ created, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!created) return null;
  const text = `Correo: ${created.email}\nContraseña temporal: ${created.temp_password}`;
  return (
    <Dialog open={!!created} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Distribuidor creado ✅</DialogTitle>
          <DialogDescription>Comparte estos accesos con {created.name}. Podrá cambiar su contraseña más adelante.</DialogDescription></DialogHeader>
        <div className="rounded-xl bg-xuma-dash border border-xuma-border p-4 text-sm">
          <p><span className="text-xuma-muted">Correo:</span> <b>{created.email}</b></p>
          <p className="mt-1"><span className="text-xuma-muted">Contraseña temporal:</span> <b>{created.temp_password}</b></p>
          <p className="mt-1"><span className="text-xuma-muted">Tarjeta:</span> /c/{created.card_slug}</p>
        </div>
        <DialogFooter>
          <Button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }} variant="outline" className="gap-1.5">
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />} Copiar accesos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Kpi = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl bg-white border border-xuma-border p-5">
    <Icon className="h-5 w-5 text-xuma-primary" />
    <p className="mt-3 font-heading font-extrabold text-2xl text-xuma-dark">{value}</p>
    <p className="text-sm text-xuma-muted">{label}</p>
  </div>
);

const Table = ({ headers, children }) => (
  <div className="rounded-2xl bg-white border border-xuma-border overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-xuma-border text-left text-xs uppercase tracking-wider text-xuma-muted">
          {headers.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
        </tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  </div>
);

const EmptyRow = ({ cols, text }) => (
  <tr><td colSpan={cols} className="px-4 py-10 text-center text-xuma-muted">{text}</td></tr>
);
